import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Mail,
  UserPlus,
  UserCheck,
  UserX,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { companyApi } from '../../services/api';
import { authService } from '../../services/authService';
import ConfirmationDialog from '../common/ConfirmationDialog';

interface User {
  id: string;
  email: string;
  displayName?: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

interface Invitation {
  id: string;
  inviteEmail: string;
  inviteStatus: 'pending' | 'accepted' | 'expired' | 'cancelled';
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
}

interface UserManagementProps {
  companyId: string;
}

const UserManagement: React.FC<UserManagementProps> = ({ companyId }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    title?: string;
    message?: React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    icon?: React.ReactNode;
    onConfirm?: () => void | Promise<void>;
  }>({ open: false });
  const [dialogConfirming, setDialogConfirming] = useState(false);
  const currentUserId = authService.getCurrentCompanyUser()?.id ?? null;

  // データを読み込み
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersResponse, invitationsResponse] = await Promise.all([
        companyApi.getCompanyUsers(companyId),
        companyApi.getCompanyInvitations(companyId),
      ]);

      if (usersResponse.success && Array.isArray(usersResponse.data)) {
        setUsers(usersResponse.data);
      } else {
        console.warn('Users data is not an array:', usersResponse.data);
        setUsers([]);
      }

      if (invitationsResponse.success && Array.isArray(invitationsResponse.data)) {
        setInvitations(invitationsResponse.data);
      } else {
        console.warn('Invitations data is not an array:', invitationsResponse.data);
        setInvitations([]);
      }
    } catch (error) {
      console.error('データの読み込みエラー:', error);
      setMessage({ type: 'error', text: 'データの読み込みに失敗しました' });
      setUsers([]);
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadData();
  }, [companyId, loadData]);

  // メッセージを3秒後に自動削除
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // 招待送信
  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      setMessage({ type: 'error', text: 'メールアドレスを入力してください' });
      return;
    }

    try {
      setInviting(true);
      const response = await companyApi.inviteUser(companyId, inviteEmail.trim());

      if (response.success) {
        setMessage({ type: 'success', text: '招待メールを送信しました' });
        setInviteEmail('');
        loadData(); // データを再読み込み
      } else {
        setMessage({ type: 'error', text: response.message || '招待の送信に失敗しました' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '招待の送信中にエラーが発生しました' });
    } finally {
      setInviting(false);
    }
  };

  // 招待キャンセル
  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const response = await companyApi.cancelInvitation(invitationId);

      if (response.success) {
        setMessage({ type: 'success', text: '招待をキャンセルしました' });
        loadData(); // データを再読み込み
      } else {
        setMessage({ type: 'error', text: response.message || '招待のキャンセルに失敗しました' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '招待のキャンセル中にエラーが発生しました' });
    }
  };

  // 招待再送信
  const handleResendInvitation = async (inviteEmail: string) => {
    try {
      const response = await companyApi.resendInvitation(companyId, inviteEmail);

      if (response.success) {
        setMessage({ type: 'success', text: '招待メールを再送信しました' });
        loadData(); // データを再読み込み
      } else {
        setMessage({ type: 'error', text: response.message || '招待の再送信に失敗しました' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '招待の再送信中にエラーが発生しました' });
    }
  };

  // 参加中ユーザー削除
  const handleDeleteUserButtonClick = async (user: User) => {
    setDialogState({
      open: true,
      title: 'ユーザー削除の確認',
      message: (
        <div className="space-y-2">
          <p>この管理ユーザーを削除しますか？</p>
          <p className="text-sm text-gray-600">対象: {user.displayName || user.email}</p>
          <p className="text-sm text-gray-600">メールアドレス: {user.email}</p>
          <p className="text-sm text-red-600">削除後、このユーザーはログインできなくなります。</p>
        </div>
      ),
      confirmText: '削除する',
      cancelText: 'キャンセル',
      onConfirm: async () => {
        try {
          setDialogConfirming(true);
          const response = await companyApi.deleteCompanyUser(user.id);

          if (response.success) {
            setMessage({ type: 'success', text: 'ユーザーを削除しました' });
            await loadData();
          } else {
            setMessage({ type: 'error', text: response.message || 'ユーザーの削除に失敗しました' });
          }
        } catch (error) {
          setMessage({ type: 'error', text: 'ユーザーの削除中にエラーが発生しました' });
        } finally {
          setDialogState({ open: false });
          setDialogConfirming(false);
        }
      },
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expired':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '招待中';
      case 'accepted':
        return '受諾済み';
      case 'expired':
        return '期限切れ';
      case 'cancelled':
        return 'キャンセル済み';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleResetPasswordButtonClick = async (email: string) => {
    setDialogState({
      open: true,
      title: '確認',
      message: `パスワードリセットを行いますか？\n\n対象: ${email}`,
      confirmText: 'はい',
      cancelText: 'いいえ',
      onConfirm: async () => {
        try {
          setDialogConfirming(true);
          await authService.resetPassword(email);
          setMessage({ type: 'success', text: 'パスワードリセットメールを送信しました' });
        } catch (error) {
          setMessage({ type: 'error', text: 'パスワードリセットの送信に失敗しました' });
        } finally {
          setDialogState({ open: false });
          setDialogConfirming(false);
        }
      },
    });
  };

  return (
    <div className="space-y-8">
      {/* メッセージ表示 */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* 新規招待セクション */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <UserPlus className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">新規ユーザー招待</h3>
          </div>
        </div>
        <div className="p-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                招待するメールアドレス
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={inviting}
                onKeyPress={(e) => e.key === 'Enter' && handleInvite()}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleInvite}
                disabled={inviting || !inviteEmail.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {inviting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>送信中...</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    <span>招待する</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 参加中ユーザー一覧 */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                参加中ユーザー ({users.length})
              </h3>
            </div>
            <button
              onClick={loadData}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors flex items-center space-x-1"
            >
              <RefreshCw className="w-4 h-4" />
              <span>更新</span>
            </button>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {users.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>参加中のユーザーはいません</p>
            </div>
          ) : (
            users.map((user) => (
              <div key={user.id} className="p-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      user.isActive ? 'bg-green-100' : 'bg-gray-100'
                    }`}
                  >
                    {user.isActive ? (
                      <UserCheck className="w-5 h-5 text-green-600" />
                    ) : (
                      <UserX className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900">
                        {user.displayName || user.email}
                      </h4>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          user.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {user.isActive ? '有効' : '無効'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{user.email}</p>
                    <p className="text-xs text-gray-500">
                      最終ログイン: {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'なし'}
                    </p>
                    <button
                      className="px-2 text-sm  bg-red-600 text-white rounded-md hover:bg-red-700  disabled:bg-gray-300  disabled:text-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      disabled={!user.isActive}
                      onClick={() => handleResetPasswordButtonClick(user.email)}
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      <span>パスワードリセット</span>
                    </button>
                    <button
                      className="mt-2 px-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center"
                      disabled={user.id === currentUserId}
                      onClick={() => handleDeleteUserButtonClick(user)}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      <span>{user.id === currentUserId ? '削除不可(自分)' : 'ユーザー削除'}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 招待一覧 */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Mail className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold text-gray-900">招待一覧 ({invitations.length})</h3>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {invitations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>送信済みの招待はありません</p>
            </div>
          ) : (
            invitations.map((invitation) => (
              <div key={invitation.id} className="p-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    {getStatusIcon(invitation.inviteStatus)}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900">{invitation.inviteEmail}</h4>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          invitation.inviteStatus === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : invitation.inviteStatus === 'accepted'
                              ? 'bg-green-100 text-green-800'
                              : invitation.inviteStatus === 'expired'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {getStatusText(invitation.inviteStatus)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      送信日: {formatDate(invitation.createdAt)}
                    </p>
                    {invitation.inviteStatus === 'pending' && (
                      <p className="text-xs text-gray-500">
                        有効期限: {formatDate(invitation.expiresAt)}
                      </p>
                    )}
                    {invitation.acceptedAt && (
                      <p className="text-xs text-gray-500">
                        受諾日: {formatDate(invitation.acceptedAt)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {invitation.inviteStatus === 'pending' && (
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors flex items-center space-x-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>キャンセル</span>
                    </button>
                  )}
                  {(invitation.inviteStatus === 'expired' ||
                    invitation.inviteStatus === 'cancelled') && (
                    <button
                      onClick={() => handleResendInvitation(invitation.inviteEmail)}
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md transition-colors flex items-center space-x-1"
                    >
                      <RefreshCw className="w-3 h-3" />
                      <span>再送信</span>
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 確認ダイアログ */}
      <ConfirmationDialog
        open={dialogState.open}
        onCancel={() => {
          if (!dialogConfirming) setDialogState((prev) => ({ ...prev, open: false }));
        }}
        onConfirm={dialogState.onConfirm || (() => {})}
        title={dialogState.title || '確認'}
        message={dialogState.message}
        confirmText={dialogState.confirmText || 'OK'}
        cancelText={dialogState.cancelText || 'キャンセル'}
        icon={dialogState.icon}
        autoFocusConfirm={true}
        isConfirming={dialogConfirming}
      />
    </div>
  );
};

export default UserManagement;
